import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ParticipantAuctionView.css";
// Shares the auctioneer view's card/header design so both screens look alike.
import "../MyCreatedAuctions/MyCreatedAuctionView.css";
import { format, parse, isValid, addMinutes } from "date-fns";
import {
  Calendar,
  Clock,
  Users,
  FileText,
  Download,
  IndianRupee,
  Timer,
  ArrowLeft,
  RefreshCw,
  Play,
  Gavel,
  CheckCircle,
  XCircle,
  Phone,
  Mail,
  Building2,
  Trophy,
  UserCheck,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { API_BASE_URL } from "../../../services/apiConfig";
import SuccessModal from "../../Common/SuccessModal";
import ErrorModal from "../../Common/ErrorModal";
import InfoModal from "../../Common/InfoModal";
import { auctionTypeLabel, auctionModeLabel } from "../../../utils/auctionTypeLabels";
import { stepFieldLabel } from "../../../utils/auctionDirection";

// Utility to format time string
const formatTime = (timeStr: string | undefined) => {
  if (!timeStr) return "N/A";

  try {
    let parsed = parse(timeStr, "HH:mm:ss", new Date());
    if (!isValid(parsed)) {
      parsed = parse(timeStr, "HH:mm", new Date());
    }
    if (!isValid(parsed)) {
      const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (timeMatch) {
        const [, hours, minutes] = timeMatch;
        parsed = parse(`${hours}:${minutes}`, "HH:mm", new Date());
      }
    }

    if (isValid(parsed)) {
      return format(parsed, "hh:mm a");
    }
  } catch (error) {
    console.warn("[formatTime] Error parsing time:", timeStr, error);
  }

  return timeStr.replace(/:\d{2}$/, "");
};

// Utility to format duration
const formatDuration = (duration: number | undefined) => {
  if (!duration || duration === 0) return "N/A";

  const minutes = duration > 1440 ? Math.floor(duration / 60) : duration;

  if (minutes < 60) {
    return `${minutes} minutes`;
  } else {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours} hours`;
  }
};

interface WinnerInfo {
  user_id: number;
  person_name: string;
  company_name: string;
  amount: number;
}

interface CreatorInfo {
  company_name: string;
  person_name: string;
  phone: string;
  email: string;
}

interface AuctionData {
  id: number;
  auction_title: string;
  auction_number: string;
  auction_date: string;
  start_time: string;
  end_time: string;
  duration: number;
  currency: string;
  description: string;
  pre_bid_allowed: number;
  decremental_value: number;
  starting_price: number;
  reserve_price: number | null;
  status: "upcoming" | "live" | "completed";
  auction_type?: string;
  auction_mode?: string;
  documents: any[];
  created_by: number;
  created_at: string;
  updated_at: string;
  bids: any[];
  participants: any[];
  winner_info?: WinnerInfo | null;
  creator_info?: CreatorInfo | null;
}

interface Document {
  id?: number;
  file_name?: string;
  file_url?: string;
  file_type?: string;
  name?: string;
  url?: string;
  document_url?: string;
  document_name?: string;
  document_type?: string;
}

const ParticipantAuctionView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [auction, setAuction] = useState<AuctionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Modal states for notifications
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    message: "",
    title: "Success",
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: "",
    title: "Error",
  });
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    message: "",
    title: "Information",
  });

  // Pre-bid functionality states - now inline
  const [preBuildInput, setPreBuildInput] = useState("");
  const [preBuildError, setPreBuildError] = useState<string | null>(null);
  const [preBuildLoading, setPreBuildLoading] = useState(false);

  const isMountedRef = useRef(true);
  const previousStatusRef = useRef<string | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (id) {
      loadAuctionDetails();
    }
  }, [id]);

  // Real-time status update polling
  useEffect(() => {
    if (!auction || !id) return;

    // Function to check if status should change based on current time
    const checkStatusChange = () => {
      const now = new Date();
      const auctionDate = new Date(auction.auction_date);
      
      // Parse start and end times
      const [startHours, startMinutes] = auction.start_time.split(':').map(Number);
      const [endHours, endMinutes] = auction.end_time.split(':').map(Number);
      
      const startDateTime = new Date(auctionDate);
      startDateTime.setHours(startHours, startMinutes, 0, 0);
      
      const endDateTime = new Date(auctionDate);
      endDateTime.setHours(endHours, endMinutes, 0, 0);

      // Determine what status should be based on current time
      let expectedStatus: "upcoming" | "live" | "completed";
      if (now < startDateTime) {
        expectedStatus = "upcoming";
      } else if (now >= startDateTime && now < endDateTime) {
        expectedStatus = "live";
      } else {
        expectedStatus = "completed";
      }

      // If status doesn't match expected, refresh auction data
      if (auction.status !== expectedStatus) {
        loadAuctionDetails(true);
      }
    };

    // Check status immediately
    checkStatusChange();

    // Poll every 10 seconds to check for status changes
    const statusCheckInterval = setInterval(() => {
      if (isMountedRef.current) {
        checkStatusChange();
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(statusCheckInterval);
  }, [id, auction]);

  // Timer to trigger status change at exact start/end time
  useEffect(() => {
    if (!auction) return;

    const now = new Date();
    const auctionDate = new Date(auction.auction_date);
    
    // Parse start and end times
    const [startHours, startMinutes] = auction.start_time.split(':').map(Number);
    const [endHours, endMinutes] = auction.end_time.split(':').map(Number);
    
    const startDateTime = new Date(auctionDate);
    startDateTime.setHours(startHours, startMinutes, 0, 0);
    
    const endDateTime = new Date(auctionDate);
    endDateTime.setHours(endHours, endMinutes, 0, 0);

    const timers: NodeJS.Timeout[] = [];

    // If auction is upcoming, set timer for start
    if (auction.status === "upcoming" && now < startDateTime) {
      const timeUntilStart = startDateTime.getTime() - now.getTime();
      if (timeUntilStart > 0 && timeUntilStart < 24 * 60 * 60 * 1000) { // Within 24 hours
        const startTimer = setTimeout(() => {
          loadAuctionDetails(true);
        }, timeUntilStart);
        timers.push(startTimer);
      }
    }

    // If auction is live or upcoming, set timer for end
    if ((auction.status === "live" || auction.status === "upcoming") && now < endDateTime) {
      const timeUntilEnd = endDateTime.getTime() - now.getTime();
      if (timeUntilEnd > 0 && timeUntilEnd < 24 * 60 * 60 * 1000) { // Within 24 hours
        const endTimer = setTimeout(() => {
          loadAuctionDetails(true);
        }, timeUntilEnd);
        timers.push(endTimer);
      }
    }

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [auction]);

  const getAuthToken = () => {
    return (
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken")
    );
  };

  const loadAuctionDetails = async (isManualRefresh = false) => {
    if (!id || !isMountedRef.current) {
      navigate("/dashboard/auctions");
      return;
    }

    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }


      // Fetch auction details from the correct API endpoint
      const response = await fetch(`${API_BASE_URL}/auction/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);

        if (response.status === 403) {
          const errorMessage =
            errorData?.message || "You do not have access to this auction";
          console.error("403 Access Denied:", {
            auctionId: id,
            userPhone: user?.phoneNumber,
            userId: user?.id,
            errorMessage,
          });

          setErrorModal({
            isOpen: true,
            message: `Access Denied: ${errorMessage}. This is a private auction and you need to be invited by the auctioneer to view it. Please contact the auctioneer if you believe you should have access.`,
            title: "Private Auction - Access Denied",
          });

          // Redirect to auctions list after showing error
          setTimeout(() => navigate("/dashboard/MyParticipatedA"), 3000);
          return;
        }

        throw new Error(
          errorData?.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();

      if (result.success && result.auction) {
        const auctionData = result.auction;

        // Use statistics for bidding info since starting_price might not be in the main response
        const statistics = result.statistics || {};

        // Transform the API data to match our interface
        const transformedAuction: AuctionData = {
          id: auctionData.id,
          auction_title: auctionData.title || "Untitled Auction",
          auction_number: auctionData.auction_no || `AUC-${auctionData.id}`,
          auction_date: auctionData.auction_date || "",
          start_time:
            auctionData.start_time || auctionData.formatted_start_time || "",
          end_time:
            auctionData.status === "upcoming"
              ? (() => {
                  const s = parse(
                    auctionData.start_time!,
                    "HH:mm:ss",
                    new Date()
                  );
                  const e = addMinutes(s, auctionData.duration || 0);
                  return format(e, "hh:mm a");
                })()
              : auctionData.formatted_end_time || auctionData.end_time || "",
          duration: parseInt(auctionData.duration) || 0,
          currency: auctionData.currency || "INR",
          description: auctionData.description || "No description available",
          pre_bid_allowed: auctionData.pre_bid_allowed ? 1 : 0,
          decremental_value: parseFloat(auctionData.decremental_value) || 0,
          starting_price:
            parseFloat(auctionData.current_price) ||
            parseFloat(statistics.highest_bid) ||
            0,
          reserve_price: auctionData.reserve_price
            ? parseFloat(auctionData.reserve_price)
            : null,
          status: auctionData.status || "upcoming",
          auction_type: auctionData.auction_type,
          auction_mode: auctionData.auction_mode,
          documents: transformDocuments(
            result.documents || auctionData.documents || []
          ),
          created_by: auctionData.created_by,
          created_at: auctionData.created_at,
          updated_at: auctionData.updated_at,
          bids: Array.isArray(result.bids) ? result.bids : [],
          participants: Array.isArray(result.participants)
            ? result.participants
            : [],
          winner_info: auctionData.winner_info || null,
          creator_info: auctionData.creator_info || null,
        };

        if (isMountedRef.current) {
          // Check if status has changed and show notification
          if (previousStatusRef.current && previousStatusRef.current !== transformedAuction.status) {
            const statusMessages = {
              live: "🔴 Auction is now LIVE! You can start bidding.",
              completed: "✅ Auction has ended. Thank you for participating!",
              upcoming: "⏳ Auction status updated to Upcoming."
            };
            
            setInfoModal({
              isOpen: true,
              title: "Auction Status Updated",
              message: statusMessages[transformedAuction.status] || "Auction status has been updated.",
            });
          }
          
          previousStatusRef.current = transformedAuction.status;
          setAuction(transformedAuction);
        }
      } else {
        throw new Error(result.message || "Failed to load auction data");
      }
    } catch (error) {
      console.error(
        "[ParticipantAuctionView] Error loading auction details:",
        error
      );
      if (isMountedRef.current) {
        setErrorModal({
          isOpen: true,
          message:
            "Failed to load auction details. Please try refreshing the page.",
          title: "Error Loading Auction",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  // Helper function to transform documents data from API response
  const transformDocuments = (documents: any[]): Document[] => {
    if (!documents || !Array.isArray(documents)) {
      return [];
    }

    return documents
      .map((doc: any, index: number) => {
        // Handle different API response formats
        const transformedDoc: Document = {
          id: doc.id || index,
          file_name:
            doc.file_name ||
            doc.name ||
            doc.document_name ||
            `Document ${index + 1}`,
          file_url: doc.file_url || doc.url || doc.document_url,
          file_type: doc.file_type || doc.type || doc.document_type || "PDF",
          name:
            doc.file_name ||
            doc.name ||
            doc.document_name ||
            `Document ${index + 1}`,
          url: doc.file_url || doc.url || doc.document_url,
          document_url: doc.file_url || doc.url || doc.document_url,
          document_name: doc.file_name || doc.name || doc.document_name,
          document_type:
            doc.file_type || doc.type || doc.document_type || "PDF",
        };

        return transformedDoc;
      })
      .filter((doc) => doc.file_url || doc.url || doc.document_url); // Filter out documents without URLs
  };

  const handleDownloadDocument = async (doc: Document) => {
    
    const documentUrl = doc.file_url || doc.url || doc.document_url;
    const filename = doc.file_name || doc.name || doc.document_name || "document";

    // Validate document URL
    if (!documentUrl) {
      setErrorModal({
        isOpen: true,
        message: "Document URL not available. Please contact the auctioneer.",
        title: "Document Error",
      });
      console.error("Document URL is missing:", doc);
      return;
    }

    try {
      // Construct full URL if it's a relative path
      let fullUrl = documentUrl.trim();
      if (!fullUrl.startsWith("http")) {
        fullUrl = fullUrl.startsWith("/")
          ? `${API_BASE_URL}${fullUrl}`
          : `${API_BASE_URL}/${fullUrl}`;
      }
      
      // CRITICAL FIX: Ensure HTTPS is used to avoid Mixed Content errors
      if (fullUrl.startsWith('http://')) {
        fullUrl = fullUrl.replace('http://', 'https://');
      }


      const token = getAuthToken();

      // Fetch the file as a blob
      const response = await fetch(fullUrl, {
        method: "GET",
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("File not found on server");
        } else if (response.status === 403 || response.status === 401) {
          throw new Error("Access denied - please check authentication");
        } else {
          throw new Error(`Server error: ${response.status}`);
        }
      }

      // Get the blob from response
      const blob = await response.blob();

      // Check if we got actual file content
      if (blob.size === 0) {
        throw new Error("Empty file received from server");
      }

      // Ensure proper filename with extension
      let finalFilename = filename;
      if (!finalFilename.includes(".")) {
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("pdf")) {
          finalFilename += ".pdf";
        } else if (contentType?.includes("image")) {
          finalFilename += ".jpg";
        } else if (
          contentType?.includes("excel") ||
          contentType?.includes("spreadsheet")
        ) {
          finalFilename += ".xlsx";
        } else if (contentType?.includes("word")) {
          finalFilename += ".docx";
        } else {
          finalFilename += ".pdf";
        }
      }

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = finalFilename; // This forces download instead of preview
      a.style.display = "none";
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      setSuccessModal({
        isOpen: true,
        message: `Downloaded ${finalFilename} successfully!`,
        title: "Download Started",
      });

    } catch (error: any) {
      console.error("Download error:", error);
      setErrorModal({
        isOpen: true,
        message: error.message || `Failed to download ${filename}. Please try again later.`,
        title: "Document Access Error",
      });
    }
  };

  const handleJoinAuction = async () => {
    if (!auction || !user) return;

    try {
      const token = getAuthToken();
      if (!token) {
        setErrorModal({
          isOpen: true,
          message: "Authentication required. Please log in to continue.",
          title: "Authentication Required",
        });
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/auction/${auction.id}/participants`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phone_number: user.phoneNumber,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSuccessModal({
            isOpen: true,
            message: "Successfully joined the auction!",
            title: "Auction Joined",
          });
          loadAuctionDetails();
        } else {
          setErrorModal({
            isOpen: true,
            message: result.message || "Failed to join auction",
            title: "Join Auction Error",
          });
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error joining auction:", error);
      setErrorModal({
        isOpen: true,
        message: "Failed to join auction. Please try again.",
        title: "Join Auction Error",
      });
    }
  };

  const handleSavePreBuild = async () => {
    if (!auction) return;
    setPreBuildError(null);

    const val = Number(preBuildInput);

    if (isNaN(val) || val <= 0) {
      setPreBuildError("Please enter a valid positive number");
      return;
    }

    // REMOVED: All pre-bid validation logic - accept any positive bid amount

    setPreBuildLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        setErrorModal({
          isOpen: true,
          message: "Authentication required. Please log in to continue.",
          title: "Authentication Required",
        });
        return;
      }

      // Use the same API endpoint for both pre-bids and live bids
      const response = await fetch(`${API_BASE_URL}/auction/bid`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          auction_id: auction.id,
          amount: val,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success || result.status === true) {
          setSuccessModal({
            isOpen: true,
            message:
              auction.status === "upcoming"
                ? "Pre-bid submitted successfully!"
                : "Bid placed successfully!",
            title: "Bid Submitted",
          });
          setPreBuildInput("");
          loadAuctionDetails();
        } else {
          setPreBuildError(result.message || "Failed to submit bid");
        }
      } else {
        const errorResult = await response.json().catch(() => null);
        throw new Error(
          errorResult?.message || `HTTP error! status: ${response.status}`
        );
      }
    } catch (error) {
      console.error("Error submitting bid:", error);
      setPreBuildError(
        error instanceof Error ? error.message : "Failed to submit bid"
      );
      setErrorModal({
        isOpen: true,
        message: "Failed to submit bid. Please try again.",
        title: "Bid Submission Error",
      });
    } finally {
      setPreBuildLoading(false);
    }
  };

  const formatCurrency = (amount: number | undefined, currency: string) => {
    return `${currency} ${amount?.toLocaleString() || 0}`;
  };

  if (loading) {
    return (
      <div className="auction-view-container">
        <div className="loading-spinner">
          <RefreshCw className="animate-spin w-8 h-8 text-blue-600" />
          <p>Loading auction details...</p>
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="auction-view-container">
        <div className="error-state">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">
            Auction Not Found
          </h2>
          <p className="text-gray-500 mb-4">
            The requested auction could not be found.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => navigate("/dashboard/MyParticipatedA")}
              className="btn btn-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Auctions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate bidding information
  const activeBids = auction.bids.filter(
    (bid: any) =>
      bid.status === "approved" ||
      bid.status === null ||
      bid.status === undefined
  );
  const currentLowestBid =
    activeBids.length > 0
      ? Math.min(
          ...activeBids.map((bid: any) =>
            parseFloat(bid.amount || auction.starting_price)
          )
        )
      : auction.starting_price;
  const totalBids = activeBids.length;

  // CORRECTED: Minimum allowed bid = Current Lowest Bid - Decremental Value
  const minAllowedBid = currentLowestBid - auction.decremental_value;

  // Check if user is participant
  const isParticipant = auction.participants.some(
    (p: any) => p.phone_number === user?.phoneNumber || p.user_id === user?.id
  );

  const isWinner =
    !!auction.winner_info && !!user && auction.winner_info.user_id === Number(user.id);
  const statusClass =
    auction.status === "live" ? "live" : auction.status === "completed" ? "completed" : "upcoming";
  const stepLabel = stepFieldLabel(auction.auction_mode);
  const dateLabel = auction.auction_date
    ? format(new Date(auction.auction_date), "dd/MM/yyyy")
    : "Date not specified";

  return (
    <div className="view-auction-container mcv-page">
      <button
        type="button"
        onClick={() => navigate("/dashboard/MyParticipatedA")}
        className="mcv-back"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Auctions
      </button>

      {/* Header */}
      <header className="mcv-header">
        <div className="mcv-header-info">
          <h1 className="mcv-title">{auction.auction_title}</h1>
          <div className="mcv-meta">
            <span className="mcv-auction-no">
              <Gavel /> {auction.auction_number}
            </span>
            <span className="mcv-meta-text">
              {dateLabel} · {formatTime(auction.start_time)}
              {auction.end_time ? ` – ${formatTime(auction.end_time)}` : ""}
            </span>
          </div>
          <div className="mcv-tags">
            <span className="mcv-tag mcv-tag-type">{auctionTypeLabel(auction.auction_type)}</span>
            <span className="mcv-tag mcv-tag-mode">{auctionModeLabel(auction.auction_mode)}</span>
          </div>
        </div>
        <span className={`mcv-status mcv-status-${statusClass}`}>
          <span className="mcv-status-dot" />
          {auction.status.toUpperCase()}
        </span>
      </header>

      {/* Key figures -- nothing about other bidders or participants */}
      <section className="mcv-stats">
        <div className="mcv-stat mcv-stat-lead">
          <span className="mcv-stat-label">Your Registration</span>
          <span className="mcv-stat-value">{isParticipant ? "Registered" : "Not registered"}</span>
          <span className="mcv-stat-hint">
            {isParticipant ? "You are registered for this auction" : "Ask the auctioneer for an invite"}
          </span>
        </div>
        <div className="mcv-stat">
          <span className="mcv-stat-label">Duration</span>
          <span className="mcv-stat-value">{formatDuration(auction.duration)}</span>
          <span className="mcv-stat-hint">Total running time</span>
        </div>
        <div className="mcv-stat">
          <span className="mcv-stat-label">{stepLabel}</span>
          <span className="mcv-stat-value">
            {auction.decremental_value
              ? formatCurrency(auction.decremental_value, auction.currency)
              : "—"}
          </span>
          <span className="mcv-stat-hint">Minimum bid change</span>
        </div>
        <div className="mcv-stat">
          <span className="mcv-stat-label">Pre-Bid</span>
          <span className="mcv-stat-value">{auction.pre_bid_allowed ? "Allowed" : "Not allowed"}</span>
          <span className="mcv-stat-hint">Before the auction goes live</span>
        </div>
      </section>

      <div className="mcv-layout">
        <div className="mcv-main">
          {/* Description */}
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <FileText /> Auction Details / Description
              </h2>
            </div>
            <div className="mcv-card-body">
              <p className="mcv-description">{auction.description || "No description provided."}</p>
            </div>
          </section>

          {/* Documents */}
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <FileText /> Auction Documents
                {auction.documents && auction.documents.length > 0 && (
                  <span className="mcv-count">{auction.documents.length}</span>
                )}
              </h2>
            </div>
            {auction.documents && auction.documents.length > 0 ? (
              <ul className="mcv-list">
                {auction.documents.map((doc, index) => {
                  const docName = doc.file_name || doc.name || `Document ${index + 1}`;
                  return (
                    <li key={doc.id || index} className="mcv-row">
                      <div className="mcv-file-icon">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="mcv-row-main">
                        <div className="mcv-row-title" title={docName}>
                          {docName}
                        </div>
                        <div className="mcv-row-sub">{doc.file_type || "PDF Document"}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDownloadDocument(doc)}
                        className="mcv-btn"
                        title={`Download ${docName}`}
                        disabled={!doc.file_url && !doc.url && !doc.document_url}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="mcv-empty">
                <FileText />
                <h3>No Documents Available</h3>
                <p>No documents have been uploaded for this auction.</p>
              </div>
            )}
          </section>

          {/* Pre-bid -- only while the auction is upcoming */}
          {auction.pre_bid_allowed && auction.status === "upcoming" && (
            <section className="mcv-card">
              <div className="mcv-card-header">
                <h2 className="mcv-card-title">
                  <Gavel /> Pre-Bid Submission
                </h2>
              </div>
              <div className="mcv-card-body prebuild-content">
                <p className="bid-description">
                  Submit your bid before the auction goes live. You can place any bid amount you wish.
                </p>
                <div className="bid-form-wrapper">
                  <div className="bid-input-group">
                    <label className="bid-input-label">Enter your bid amount ({auction.currency})</label>
                    <input
                      type="number"
                      value={preBuildInput}
                      onChange={(e) => {
                        setPreBuildInput(e.target.value);
                        setPreBuildError(null);
                      }}
                      placeholder={`Enter bid amount (${auction.currency})`}
                      className="bid-input"
                      disabled={preBuildLoading}
                    />
                  </div>

                  {preBuildError && (
                    <div className="bid-error-message">
                      {preBuildError.split("\n").map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                    </div>
                  )}

                  <div className="bid-action-buttons">
                    <button
                      onClick={handleSavePreBuild}
                      disabled={preBuildLoading || !preBuildInput}
                      className="submit-bid-btn"
                    >
                      <Gavel className="w-4 h-4" />
                      {preBuildLoading ? "Submitting..." : "Submit Pre-Bid"}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Live */}
          {auction.status === "live" && (
            <section className="mcv-card">
              <div className="mcv-card-header">
                <h2 className="mcv-card-title">
                  <Play /> Auction is Live
                </h2>
              </div>
              <div className="mcv-card-body prebuild-content">
                <p className="bid-description" style={{ textAlign: "center", fontSize: "16px", color: "#059669" }}>
                  🔴 This auction is now LIVE! Pre-bidding is closed.
                  <br />
                  Please go to the live auction session to place your bids.
                </p>
                <div className="bid-action-buttons" style={{ marginTop: "1rem" }}>
                  <button
                    onClick={() => navigate(`/dashboard/auction-session/${auction.id}`)}
                    className="submit-bid-btn"
                    style={{ background: "#059669" }}
                  >
                    <Play className="w-4 h-4" />
                    Join Live Auction
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Result */}
          {auction.status === "completed" && (
            <section className="mcv-card">
              <div className="mcv-card-header">
                <h2 className="mcv-card-title">
                  <CheckCircle /> Auction Completed
                </h2>
              </div>
              <div className="mcv-card-body">
                {/* Winner details are shown only to the winner */}
                {isWinner && auction.winner_info ? (
                  <>
                    <div
                      style={{
                        textAlign: "center",
                        padding: "1.5rem 1rem",
                        background: "rgba(16, 185, 129, 0.12)",
                        borderRadius: 12,
                        border: "1px solid rgba(16, 185, 129, 0.4)",
                        marginBottom: "1.25rem",
                      }}
                    >
                      <Trophy
                        style={{ width: 52, height: 52, color: "#10b981", margin: "0 auto 0.75rem", display: "block" }}
                      />
                      <h3 style={{ fontSize: 20, fontWeight: 700, color: "#10b981", margin: "0 0 0.5rem" }}>
                        🎉 Congratulations! You Won This Auction! 🎉
                      </h3>
                      <p style={{ margin: 0, fontWeight: 600, color: "var(--text-primary)" }}>
                        Your winning bid:{" "}
                        <strong style={{ color: "#10b981" }}>
                          {formatCurrency(auction.winner_info.amount, auction.currency)}
                        </strong>
                      </p>
                    </div>

                    {auction.creator_info && (
                      <>
                        <h4
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            margin: "0 0 0.75rem",
                            fontSize: 16,
                            color: "var(--text-primary)",
                          }}
                        >
                          <UserCheck style={{ width: 18, height: 18, color: "#3b82f6" }} />
                          Contact Auctioneer for Next Steps
                        </h4>
                        <dl className="mcv-dl">
                          <div className="mcv-dl-row">
                            <dt>Company</dt>
                            <dd>{auction.creator_info.company_name || "N/A"}</dd>
                          </div>
                          <div className="mcv-dl-row">
                            <dt>Contact Person</dt>
                            <dd>{auction.creator_info.person_name || "N/A"}</dd>
                          </div>
                          <div className="mcv-dl-row">
                            <dt>Phone Number</dt>
                            <dd>
                              <a href={`tel:${auction.creator_info.phone}`} style={{ color: "#3b82f6" }}>
                                {auction.creator_info.phone}
                              </a>
                            </dd>
                          </div>
                          {auction.creator_info.email && (
                            <div className="mcv-dl-row">
                              <dt>Email</dt>
                              <dd>
                                <a href={`mailto:${auction.creator_info.email}`} style={{ color: "#3b82f6" }}>
                                  {auction.creator_info.email}
                                </a>
                              </dd>
                            </div>
                          )}
                        </dl>
                        <p style={{ margin: "1rem 0 0", fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                          📋 <strong style={{ color: "#3b82f6" }}>Next Steps:</strong> Please contact the auctioneer to
                          finalize the transaction, arrange payment, and coordinate delivery or pickup details.
                        </p>
                      </>
                    )}
                  </>
                ) : (
                  <p style={{ textAlign: "center", margin: 0, fontSize: 16, color: "var(--text-secondary)" }}>
                    {auction.winner_info
                      ? "This auction has been completed. Thank you for your participation!"
                      : "This auction has been completed without any bids placed."}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>

        <aside className="mcv-side">
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <Calendar /> Auction Information
              </h2>
            </div>
            <dl className="mcv-dl">
              <div className="mcv-dl-row">
                <dt>Auction Type</dt>
                <dd>{auctionTypeLabel(auction.auction_type)}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Format</dt>
                <dd>{auctionModeLabel(auction.auction_mode)}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Auction Date</dt>
                <dd>{dateLabel}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Start Time</dt>
                <dd>{formatTime(auction.start_time)}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>End Time</dt>
                <dd>{formatTime(auction.end_time)}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Duration</dt>
                <dd>{formatDuration(auction.duration)}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>{stepLabel}</dt>
                <dd>{formatCurrency(auction.decremental_value, auction.currency)}</dd>
              </div>
              {auction.creator_info?.company_name && (
                <div className="mcv-dl-row">
                  <dt>Hosted By</dt>
                  <dd>{auction.creator_info.company_name}</dd>
                </div>
              )}
            </dl>
          </section>
        </aside>
      </div>

      {/* Modal Components */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ ...successModal, isOpen: false })}
        title={successModal.title}
        message={successModal.message}
      />

      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
      />

      <InfoModal
        isOpen={infoModal.isOpen}
        onClose={() => setInfoModal({ ...infoModal, isOpen: false })}
        title={infoModal.title}
        message={infoModal.message}
      />
    </div>
  );
};

export default ParticipantAuctionView;


